<script lang="ts">
	import { onMount, onDestroy } from 'svelte';

	export let disabled: boolean = false;
	export let isZoomed: boolean = false;
	export let requireCtrlForWheel: boolean = false;

	// Zoom configuration
	const MIN_SCALE = 1;
	const MAX_SCALE = 5;
	const DOUBLE_TAP_SCALE = 2.5;
	const DOUBLE_TAP_THRESHOLD_MS = 300;
	const WHEEL_ZOOM_SENSITIVITY = 0.001;

	// Zoom state
	let scale = 1;
	let pointX = 0;
	let pointY = 0;

	// Pan state
	let panning = false;
	let startX = 0;
	let startY = 0;

	// Pinch state
	let pointers: PointerEvent[] = [];
	let initialDist = 0;
	let initialScale = 1;
	let isPinching = false;

	// Container state
	let containerWidth = 0;
	let containerHeight = 0;
	let lastTapTime = 0;
	let lastCenter: { x: number; y: number } | null = null;
	let containerElement: HTMLElement;

	$: isZoomed = scale > MIN_SCALE;

	function clampPosition() {
		const maxX = (containerWidth * (scale - 1)) / 2;
		const maxY = (containerHeight * (scale - 1)) / 2;

		pointX = Math.min(Math.max(pointX, -maxX), maxX);
		pointY = Math.min(Math.max(pointY, -maxY), maxY);
	}

	function getCenter(p1: PointerEvent, p2: PointerEvent) {
		return {
			x: (p1.clientX + p2.clientX) / 2,
			y: (p1.clientY + p2.clientY) / 2,
		};
	}

	function getDistance(p1: PointerEvent, p2: PointerEvent) {
		return Math.sqrt(Math.pow(p2.clientX - p1.clientX, 2) + Math.pow(p2.clientY - p1.clientY, 2));
	}

	function capturePointer(pointerId: number) {
		try {
			containerElement.setPointerCapture(pointerId);
		} catch {
			// Pointer capture can fail silently
		}
	}

	function resetZoom() {
		scale = MIN_SCALE;
		pointX = 0;
		pointY = 0;
	}

	function zoomToPoint(e: PointerEvent, targetScale: number) {
		const scaleRatio = targetScale / scale;

		if (containerElement?.firstElementChild) {
			const rect = containerElement.firstElementChild.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			pointX += (centerX - e.clientX) * (scaleRatio - 1);
			pointY += (centerY - e.clientY) * (scaleRatio - 1);
		}

		scale = targetScale;
		clampPosition();
	}

	function handleDoubleTap(e: PointerEvent): boolean {
		if (pointers.length !== 0) return false;

		const now = Date.now();
		if (now - lastTapTime < DOUBLE_TAP_THRESHOLD_MS) {
			e.stopPropagation();

			if (scale > MIN_SCALE) {
				resetZoom();
			} else {
				zoomToPoint(e, DOUBLE_TAP_SCALE);
			}

			lastTapTime = 0;
			return true;
		}

		lastTapTime = now;
		return false;
	}

	function trackPointer(e: PointerEvent) {
		const index = pointers.findIndex((p) => p.pointerId === e.pointerId);
		if (index === -1) {
			pointers.push(e);
		} else {
			pointers[index] = e;
		}
	}

	function startPanning(e: PointerEvent) {
		e.stopPropagation();
		panning = true;
		startX = e.clientX - pointX;
		startY = e.clientY - pointY;
		capturePointer(e.pointerId);
	}

	function startPinching() {
		panning = false;
		isPinching = true;
		initialDist = getDistance(pointers[0], pointers[1]);
		initialScale = scale;
		lastCenter = getCenter(pointers[0], pointers[1]);

		pointers.forEach((p) => capturePointer(p.pointerId));
	}

	function handlePointerDown(e: PointerEvent) {
		if (disabled) return;

		// Prevent compatibility mouse events (click, dblclick) from reaching
		// the video element, which would toggle play/pause on single tap.
		e.preventDefault();

		if (handleDoubleTap(e)) return;

		trackPointer(e);

		if (pointers.length === 1 && scale > MIN_SCALE) {
			startPanning(e);
		} else if (pointers.length === 2) {
			e.stopPropagation();
			startPinching();
		}
	}

	function updatePointerPosition(e: PointerEvent) {
		const index = pointers.findIndex((p) => p.pointerId === e.pointerId);
		if (index !== -1) pointers[index] = e;
	}

	function handlePinchMove(e: PointerEvent) {
		e.preventDefault();
		e.stopPropagation();

		const currentDist = getDistance(pointers[0], pointers[1]);
		const currentCenter = getCenter(pointers[0], pointers[1]);

		if (lastCenter) {
			pointX += currentCenter.x - lastCenter.x;
			pointY += currentCenter.y - lastCenter.y;
		}
		lastCenter = currentCenter;

		if (initialDist > 0) {
			const newScale = Math.min(
				Math.max(initialScale * (currentDist / initialDist), MIN_SCALE),
				MAX_SCALE,
			);

			const scaleRatio = newScale / scale;

			if (containerElement) {
				const rect = containerElement.getBoundingClientRect();
				const centerX = rect.left + rect.width / 2;
				const centerY = rect.top + rect.height / 2;

				pointX += (pointX - (currentCenter.x - centerX)) * (scaleRatio - 1);
				pointY += (pointY - (currentCenter.y - centerY)) * (scaleRatio - 1);
			}

			scale = newScale;
			clampPosition();
		}

		if (scale <= MIN_SCALE) {
			resetZoom();
		}
	}

	function handlePanMove(e: PointerEvent) {
		e.preventDefault();
		e.stopPropagation();
		pointX = e.clientX - startX;
		pointY = e.clientY - startY;
		clampPosition();
	}

	function handlePointerMove(e: PointerEvent) {
		if (disabled) return;

		updatePointerPosition(e);

		if (pointers.length === 2 && isPinching) {
			handlePinchMove(e);
		} else if (pointers.length === 1 && panning && scale > MIN_SCALE) {
			handlePanMove(e);
		}
	}

	function releasePointer(pointerId: number) {
		try {
			containerElement.releasePointerCapture(pointerId);
		} catch {
			// Pointer release can fail silently
		}
	}

	function handlePointerUp(e: PointerEvent) {
		pointers = pointers.filter((p) => p.pointerId !== e.pointerId);

		if (pointers.length < 2) {
			initialDist = 0;
			isPinching = false;
		}
		if (pointers.length === 0) {
			panning = false;
		}

		releasePointer(e.pointerId);
	}

	function handleWheel(e: WheelEvent) {
		if (disabled) return;
		if (requireCtrlForWheel && !e.ctrlKey) return;

		e.preventDefault();
		e.stopPropagation();

		const delta = -e.deltaY * WHEEL_ZOOM_SENSITIVITY;
		const newScale = Math.min(Math.max(scale + delta, MIN_SCALE), MAX_SCALE);
		const scaleRatio = newScale / scale;

		if (containerElement) {
			const rect = containerElement.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			pointX += (pointX - (e.clientX - centerX)) * (scaleRatio - 1);
			pointY += (pointY - (e.clientY - centerY)) * (scaleRatio - 1);
		}

		scale = newScale;
		clampPosition();

		if (scale <= MIN_SCALE) {
			resetZoom();
		}
	}

	// Intercept click/dblclick in capture phase so they never reach the
	// <video> element (which would toggle play/pause or fullscreen).
	// pointerdown.preventDefault() should suppress these per spec, but
	// Chrome does not fully honor it, so we catch them explicitly.
	function handleClickCapture(e: MouseEvent) {
		if (disabled) return;
		e.stopPropagation();
		e.preventDefault();
	}

	// iOS Safari: block native scroll during multi-touch/pinch gestures.
	// Must be registered with { passive: false } since Svelte's on:touchmove
	// registers passive listeners where preventDefault() is ignored.
	function handleTouchMove(e: TouchEvent) {
		if (disabled) return;
		if (e.touches.length >= 2 || isPinching || panning) {
			e.preventDefault();
		}
	}

	onMount(() => {
		containerElement?.addEventListener('touchmove', handleTouchMove, { passive: false });
	});

	onDestroy(() => {
		containerElement?.removeEventListener('touchmove', handleTouchMove);
	});
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div
	class="zoom-container"
	class:is-zoomed={isZoomed}
	data-prevent-drawer-swipe="true"
	data-prevent-sidepanel-swipe
	bind:this={containerElement}
	bind:clientWidth={containerWidth}
	bind:clientHeight={containerHeight}
	on:pointerdown={handlePointerDown}
	on:pointermove={handlePointerMove}
	on:pointerup={handlePointerUp}
	on:pointercancel={handlePointerUp}
	on:pointerleave={handlePointerUp}
	on:wheel={handleWheel}
	on:click|capture={handleClickCapture}
	on:dblclick|capture={handleClickCapture}
>
	<div class="zoom-content" style="transform: translate({pointX}px, {pointY}px) scale({scale});">
		<slot />
	</div>
</div>

<style>
	.zoom-container {
		width: 100%;
		height: 100%;
		overflow: hidden;
		touch-action: pan-y;
		position: relative;
	}
	.zoom-container.is-zoomed {
		touch-action: none;
	}
	.zoom-content {
		width: 100%;
		transform-origin: center center;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	:global(.zoom-content > *) {
		width: 100%;
	}
</style>
